import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../api';
import { useAuth } from '../context/AuthContext';

// Performance Analytics — daily activity and compliance for the IT team.
//
// Every red or amber marker is clickable and opens the records behind it, so
// "why is this person non-compliant?" is answerable without opening four
// other modules.
const STATE = {
  ok:      { icon:'✓', colour:'var(--green)', bg:'rgba(45,168,102,0.15)', label:'Completed' },
  partial: { icon:'!', colour:'#E9A23B',      bg:'rgba(233,162,59,0.18)', label:'Partially Completed' },
  fail:    { icon:'✕', colour:'var(--red)',   bg:'rgba(229,72,77,0.15)',  label:'Not Completed' },
  na:      { icon:'–', colour:'var(--text2)', bg:'var(--bg3)',            label:'Not Applicable' },
};
const BAND = { good:'var(--green)', attention:'#E9A23B', poor:'var(--red)', na:'var(--text2)' };
const iso = d => d.toISOString().slice(0, 10);
const dayLabel = d => new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short' });

export default function Performance() {
  const { user } = useAuth();
  const canSee = ['admin','it_head','it_lead'].includes(user?.role);
  const [date, setDate] = useState(iso(new Date()));
  const [rangeTo, setRangeTo] = useState(null);          // set only when a range is chosen
  const [tab, setTab] = useState('team');                 // team | person | trends
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [evidence, setEvidence] = useState(null);
  const [person, setPerson] = useState(null);

  const from = date, to = rangeTo || date;
  const load = useCallback(() => {
    setLoading(true);
    api.get('/performance/compliance', { params: { from, to } })
      .then(r => setD(r.data)).catch(() => setD(null)).finally(() => setLoading(false));
  }, [from, to]);
  useEffect(() => { if (canSee) load(); }, [load, canSee]);

  if (!canSee) return <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>IT Head / IT Lead access required</div>;

  // A person's shortcomings, in the words the Remarks column uses.
  const remarksFor = p => {
    const out = [];
    const c = p.components;
    if (c.work_report.state === 'fail') out.push('Work report not updated');
    else if (c.work_report.state === 'partial') out.push('Work report late/partial');
    if (c.erp_upload.state === 'fail') out.push('ERP calls not uploaded');
    else if (c.erp_upload.state === 'partial') out.push(`${c.erp_upload.missing_calls || ''} ERP calls missing`.trim());
    if (c.portal_activity.state === 'fail') out.push('No portal activity');
    else if (c.portal_activity.state === 'partial') out.push('Inactive some days');
    if (c.routines.state === 'fail') out.push('Routines missed');
    else if (c.routines.state === 'partial') out.push('Routine pending');
    if (p.calls.overdue > 0) out.push(`${p.calls.overdue} overdue`);
    return out.join(', ') || '-';
  };

  const exportReport = () => {
    if (!d) return;
    // One row per person — the work report belongs to a person, never a campus,
    // so the campus is context on the row rather than a grouping.
    const rows = d.people.map((p, i) => {
      const w = p.components.work_report, e = p.components.erp_upload;
      return {
      '#': i + 1, Name: p.full_name, Campus: p.campus_name,
      'Work Report': STATE[w.state].label,
      'Tickets Logged': w.logged ?? 0,
      'Closed Same Day': w.closed_same_day ?? 0,
      'Open Without Reason': w.open_no_reason ?? 0,
      'ERP Calls Uploaded': STATE[e.state].label,
      'ERP Rows Imported': e.imported ?? 0,
      'ERP Rows Failed': e.errors ?? 0,
      'Portal Activity': STATE[p.components.portal_activity.state].label,
      'Routines': STATE[p.components.routines.state].label,
      'Calls Received': p.calls.received, 'Calls Closed': p.calls.closed,
      'Overdue': p.calls.overdue,
      'Avg Resolution': p.calls.avg_hours !== null ? `${p.calls.avg_hours}h` : '-',
      'Compliance %': p.score === null ? '-' : p.score,
      Remarks: remarksFor(p),
      };
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Team Compliance');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      (d.series || []).map(s => ({ Date: s.day, 'Calls Received': s.calls }))), 'Calls Trend');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([{
      From: d.from, To: d.to, 'Team Size': d.cards.team_size, 'Calls Received': d.cards.calls_received,
      'Calls Closed': d.cards.calls_closed, 'Overdue': d.cards.overdue_calls,
      'Team Compliance %': d.cards.team_score ?? '-', Generated: new Date().toLocaleString('en-IN'),
    }]), 'Summary');
    XLSX.writeFile(wb, `IT_Performance_${from}${to !== from ? `_to_${to}` : ''}.xlsx`);
  };

  return (
    <div>
      {/* Header: title, tabs, filters */}
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12, marginBottom:14 }}>
        <div style={{ borderLeft:'4px solid var(--accent)', paddingLeft:10 }}>
          <h2 style={{ fontSize:20, fontWeight:700, marginBottom:2 }}>Performance Analytics</h2>
          <p style={{ fontSize:12.5, color:'var(--text2)' }}>Daily activity overview and compliance status of the IT team</p>
        </div>
        <div className="flex-gap" style={{ gap:8, flexWrap:'wrap', alignItems:'center' }}>
          {/* No campus filter: performance is judged per person. A person's
              campus is shown as context on their row, not used to slice the team. */}
          <input type="date" value={date} max={iso(new Date())}
            onChange={e => { setDate(e.target.value); setRangeTo(null); }} style={{ width:150 }} />
          <input type="date" value={rangeTo || ''} min={date} max={iso(new Date())}
            onChange={e => setRangeTo(e.target.value || null)} style={{ width:150 }}
            title="Optional end date — leave blank for a single day" />
        </div>
      </div>

      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', marginBottom:14, flexWrap:'wrap', gap:10 }}>
        <div style={{ display:'flex', background:'var(--bg3)', borderRadius:8, padding:3 }}>
          {/* No campus tab: work report and ERP upload belong to a person, not a
              campus, so rolling them up per campus double-counted anyone
              covering two campuses. */}
          {[['team','Team Overview'],['person','By Person'],['trends','Trends']].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              style={{ padding:'7px 16px', fontSize:12.5, cursor:'pointer', border:'none', borderRadius:6,
                background: tab === id ? 'var(--accent)' : 'transparent',
                color: tab === id ? '#fff' : 'var(--text2)', fontWeight: tab === id ? 600 : 400 }}>{label}</button>
          ))}
        </div>
        <button className="btn btn-secondary btn-sm" onClick={exportReport}>⬇ Export Report</button>
      </div>

      {loading && <div style={{ fontSize:13, color:'var(--text2)', marginBottom:10 }}>Loading…</div>}
      {!d ? null : (<>
        {tab === 'team'   && <TeamOverview d={d} setEvidence={setEvidence} remarksFor={remarksFor} onPerson={setPerson} />}
        {tab === 'person' && <ByPerson d={d} onPerson={setPerson} remarksFor={remarksFor} />}
        {tab === 'trends' && <Trends d={d} />}
      </>)}

      {evidence && <Evidence e={evidence} range={{ from, to }} onClose={() => setEvidence(null)} />}
      {person && <PersonView p={person} range={{ from, to }} remarksFor={remarksFor}
        onClose={() => setPerson(null)} onEvidence={k => setEvidence({ p: person, k })} />}
    </div>
  );
}

// ── Cards ───────────────────────────────────────────────────────────────────
const Kpi = ({ icon, tint, label, value, sub, bar, barColour }) => (
  <div className="card" style={{ margin:0, display:'flex', gap:12, alignItems:'flex-start' }}>
    <div style={{ width:42, height:42, borderRadius:10, flexShrink:0, fontSize:19,
      display:'flex', alignItems:'center', justifyContent:'center', background:tint }}>{icon}</div>
    <div style={{ flex:1, minWidth:0 }}>
      <div style={{ fontSize:12, color:'var(--text2)' }}>{label}</div>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'baseline' }}>
        <span style={{ fontSize:26, fontWeight:700 }}>{value}</span>
        {sub}
      </div>
      {bar !== undefined && (
        <div style={{ height:6, borderRadius:3, background:'var(--bg3)', marginTop:6, overflow:'hidden' }}>
          <div style={{ width:`${bar}%`, height:'100%', background:barColour }} />
        </div>
      )}
    </div>
  </div>
);

const Exception = ({ icon, label, value }) => (
  <div className="card" style={{ margin:0, display:'flex', gap:12, alignItems:'center',
    background:'rgba(229,72,77,0.06)', border:'1px solid rgba(229,72,77,0.25)' }}>
    <div style={{ width:42, height:42, borderRadius:10, flexShrink:0, fontSize:19,
      display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(229,72,77,0.15)' }}>{icon}</div>
    <div>
      <div style={{ fontSize:12.5, fontWeight:600, color:'var(--red)' }}>{label}</div>
      <div style={{ fontSize:26, fontWeight:700, color:'var(--red)' }}>{value === null ? '–' : value}</div>
    </div>
  </div>
);

// ── Donut ───────────────────────────────────────────────────────────────────
function Donut({ slices, centre, sub, size = 170 }) {
  const total = slices.reduce((n, s) => n + s.value, 0);
  const r = 56, c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg viewBox="0 0 150 150" style={{ width:size, height:size }}>
      <circle cx="75" cy="75" r={r} fill="none" stroke="var(--bg3)" strokeWidth="18" />
      {total > 0 && slices.filter(s => s.value > 0).map((s, i) => {
        const len = (s.value / total) * c;
        const el = (
          <circle key={i} cx="75" cy="75" r={r} fill="none" stroke={s.colour} strokeWidth="18"
            strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-offset}
            transform="rotate(-90 75 75)" />
        );
        offset += len;
        return el;
      })}
      <text x="75" y="72" textAnchor="middle" style={{ fontSize:26, fontWeight:700, fill:'var(--text)' }}>{centre}</text>
      <text x="75" y="90" textAnchor="middle" style={{ fontSize:10, fill:'var(--text2)' }}>{sub}</text>
    </svg>
  );
}

// ── Team Overview ───────────────────────────────────────────────────────────
function TeamOverview({ d, setEvidence, remarksFor, onPerson }) {
  const c = d.cards;
  const recv = c.calls_received;
  const pctOf = n => recv ? Math.round(n / recv * 100) : 0;
  const delta = d.prev_calls ? Math.round(((recv - d.prev_calls) / d.prev_calls) * 100) : null;
  const issues = (c.work_reports_missing || 0) + (c.erp_uploads_missing || 0)
    + c.no_portal_activity + c.missed_routines;
  const maxBar = Math.max(1, ...(d.series || []).map(s => s.calls));

  const Cell = ({ p, k }) => {
    const cc = p.components[k] || { state:'na' };
    const st = STATE[cc.state];
    return (
      <td style={{ textAlign:'center' }}>
        <button title={cc.reason ? `${st.label} — ${cc.reason}` : st.label}
          onClick={() => cc.state !== 'na' && setEvidence({ p, k })}
          style={{ border:'none', background:'transparent', fontSize:17, lineHeight:1,
            color:st.colour, cursor: cc.state !== 'na' ? 'pointer' : 'default' }}>
          {cc.state === 'ok' ? '✅' : cc.state === 'partial' ? '🟡' : cc.state === 'fail' ? '❌' : '–'}
        </button>
      </td>
    );
  };

  return (
    <>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(230px, 1fr))', gap:12, marginBottom:12 }}>
        <Kpi icon="📞" tint="rgba(79,142,247,0.15)" label="Total Calls Received" value={recv}
          sub={delta !== null && <span style={{ fontSize:11.5, color: delta >= 0 ? 'var(--green)' : 'var(--red)' }}>
            {delta >= 0 ? '↑' : '↓'} {Math.abs(delta)}%<div style={{ color:'var(--text2)' }}>vs. previous day</div></span>} />
        <Kpi icon="✅" tint="rgba(45,168,102,0.15)" label="Calls Closed" value={c.calls_closed}
          bar={pctOf(c.calls_closed)} barColour="var(--green)"
          sub={<span style={{ fontSize:11.5, color:'var(--green)' }}>{pctOf(c.calls_closed)}%</span>} />
        <Kpi icon="🕓" tint="rgba(233,162,59,0.18)" label="Calls Open" value={c.calls_open}
          bar={pctOf(c.calls_open)} barColour="#E9A23B" />
        <Kpi icon="⚠" tint="rgba(229,72,77,0.15)" label="Overdue Calls" value={c.overdue_calls}
          bar={pctOf(c.overdue_calls)} barColour="var(--red)" />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(230px, 1fr))', gap:12, marginBottom:16 }}>
        <Exception icon="📄" label="Work Reports Not Updated" value={c.work_reports_missing} />
        <Exception icon="☁" label="ERP Calls Not Uploaded" value={c.erp_uploads_missing} />
        <Exception icon="💻" label="No Portal Activity" value={c.no_portal_activity} />
        <Exception icon="🗓" label="Missed Routines" value={c.missed_routines} />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:14, marginBottom:16 }}>
        <div className="card">
          <b style={{ fontSize:13.5 }}>Calls Received</b>
          <div style={{ display:'flex', alignItems:'flex-end', gap:10, height:170, marginTop:14 }}>
            {(d.series || []).map(s => (
              <div key={s.day} style={{ flex:1, textAlign:'center' }}>
                <div style={{ fontSize:11, fontWeight:600, marginBottom:3 }}>{s.calls}</div>
                <div title={`${s.day}: ${s.calls}`}
                  style={{ height:`${Math.max(3, (s.calls / maxBar) * 120)}px`, background:'#4F8EF7',
                    borderRadius:'4px 4px 0 0' }} />
                <div style={{ fontSize:10, color:'var(--text2)', marginTop:4 }}>{dayLabel(s.day)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <b style={{ fontSize:13.5 }}>Call Status</b>
          <div className="flex-gap" style={{ alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <Donut centre={recv} sub="Total Calls" slices={[
              { value:c.calls_closed, colour:'var(--green)' },
              { value:c.calls_open,   colour:'#4F8EF7' },
              { value:c.overdue_calls, colour:'var(--red)' },
            ]} />
            <div style={{ fontSize:12.5, flex:1, minWidth:130 }}>
              {[['Closed', c.calls_closed, 'var(--green)'], ['Open', c.calls_open, '#4F8EF7'],
                ['Overdue', c.overdue_calls, 'var(--red)']].map(([l, v, col]) => (
                <div key={l} className="flex-gap" style={{ gap:7, alignItems:'center', padding:'4px 0' }}>
                  <span style={{ width:9, height:9, borderRadius:5, background:col }} />
                  <span style={{ flex:1 }}>{l}</span>
                  <b>{v}</b>
                  <span style={{ color:'var(--text2)', width:44, textAlign:'right' }}>({pctOf(v)}%)</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <b style={{ fontSize:13.5 }}>Compliance Summary</b>
          <div className="flex-gap" style={{ alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <Donut centre={issues} sub="Issues" slices={[
              { value:c.work_reports_missing || 0, colour:'var(--red)' },
              { value:c.erp_uploads_missing || 0,  colour:'#E9A23B' },
              { value:c.no_portal_activity,        colour:'#F5C518' },
              { value:c.missed_routines,           colour:'#F58220' },
            ]} />
            <div style={{ fontSize:12.5, flex:1, minWidth:150 }}>
              {[['Work report not updated', c.work_reports_missing, 'var(--red)'],
                ['ERP calls not uploaded', c.erp_uploads_missing, '#E9A23B'],
                ['No portal activity', c.no_portal_activity, '#F5C518'],
                ['Missed routines', c.missed_routines, '#F58220']].map(([l, v, col]) => (
                <div key={l} className="flex-gap" style={{ gap:7, alignItems:'center', padding:'4px 0' }}>
                  <span style={{ width:9, height:9, borderRadius:5, background:col, flexShrink:0 }} />
                  <span style={{ flex:1 }}>{l}</span>
                  <b>{v === null ? '–' : v}</b>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', padding:'10px 12px', flexWrap:'wrap', gap:8 }}>
          <b style={{ fontSize:14 }}>Team Compliance Status</b>
          <div className="flex-gap" style={{ gap:14, fontSize:11.5, color:'var(--text2)' }}>
            {['ok','fail','partial'].map(k => (
              <span key={k} className="flex-gap" style={{ gap:5, alignItems:'center' }}>
                <span style={{ width:9, height:9, borderRadius:5, background:STATE[k].colour }} />{STATE[k].label}
              </span>
            ))}
          </div>
        </div>
        <div className="table-wrap"><table>
          <thead><tr>
            <th style={{ width:30 }}>#</th><th>Name</th><th>Campus</th>
            <th style={{ textAlign:'center' }}>Work Report<br />Updated</th>
            <th style={{ textAlign:'center' }}>ERP Calls<br />Uploaded</th>
            <th style={{ textAlign:'center' }}>Portal<br />Activity</th>
            <th style={{ textAlign:'center' }}>Routines<br />Completed</th>
            <th style={{ textAlign:'center' }}>Calls<br />Received</th>
            <th style={{ textAlign:'center' }}>Calls<br />Closed</th>
            <th style={{ textAlign:'center' }}>Overdue</th>
            <th style={{ textAlign:'center' }}>Avg. Resolution<br />Time</th>
            <th>Remarks</th>
          </tr></thead>
          <tbody>
            {!d.people.length && <tr><td colSpan={12} style={{ padding:16, fontSize:13, color:'var(--text2)' }}>No IT team members match these filters.</td></tr>}
            {d.people.map((p, i) => {
              const bad = p.band === 'poor';
              return (
                <tr key={p.user_id} style={{ background: bad ? 'rgba(229,72,77,0.05)' : undefined }}>
                  <td style={{ color:'var(--text2)', fontSize:12 }}>{i + 1}</td>
                  <td><button onClick={() => onPerson(p)} style={{ background:'none', border:'none', padding:0,
                    cursor:'pointer', fontWeight:600, color:'var(--accent)', fontSize:13 }}>{p.full_name}</button></td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{p.campus_name}</td>
                  <Cell p={p} k="work_report" />
                  <Cell p={p} k="erp_upload" />
                  <Cell p={p} k="portal_activity" />
                  <Cell p={p} k="routines" />
                  <td style={{ textAlign:'center', fontSize:12.5 }}>{p.calls.received}</td>
                  <td style={{ textAlign:'center', fontSize:12.5 }}>{p.calls.closed}</td>
                  <td style={{ textAlign:'center', fontSize:12.5, color: p.calls.overdue ? 'var(--red)' : 'var(--text2)' }}>{p.calls.overdue}</td>
                  <td style={{ textAlign:'center', fontSize:12 }}>{p.calls.avg_hours !== null ? `${p.calls.avg_hours}h` : '-'}</td>
                  <td style={{ fontSize:12, color: bad ? 'var(--red)' : 'var(--text2)' }}>{remarksFor(p)}</td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
        <div style={{ padding:'8px 12px', fontSize:11.5, color:'var(--text2)', borderTop:'1px solid var(--border)' }}>
          Click a name for their full detail, or any ✕ / 🟡 to see the records behind it. A “–” means the work never
          existed that day, and never counts against anyone.
        </div>
      </div>
    </>
  );
}

// ── By Person ───────────────────────────────────────────────────────────────
function ByPerson({ d, onPerson, remarksFor }) {
  const rows = [...d.people].sort((a, b) => (a.score ?? 101) - (b.score ?? 101));
  return (
    <div className="card" style={{ padding:0, overflow:'hidden' }}>
      <b style={{ fontSize:14, display:'block', padding:'10px 12px' }}>By Person — lowest compliance first</b>
      <div className="table-wrap"><table>
        <thead><tr><th>Name</th><th>Campus</th><th style={{ textAlign:'center' }}>Compliance</th>
          <th style={{ textAlign:'center' }}>Received</th><th style={{ textAlign:'center' }}>Closed</th>
          <th style={{ textAlign:'center' }}>Overdue</th><th>Needs attention</th><th></th></tr></thead>
        <tbody>{rows.map(p => (
          <tr key={p.user_id}>
            <td style={{ fontWeight:600 }}>{p.full_name}</td>
            <td style={{ fontSize:12, color:'var(--text2)' }}>{p.campus_name}</td>
            <td style={{ textAlign:'center', fontWeight:700, color:BAND[p.band] }}>
              {p.score === null ? '–' : `${p.score}%`}</td>
            <td style={{ textAlign:'center' }}>{p.calls.received}</td>
            <td style={{ textAlign:'center' }}>{p.calls.closed}</td>
            <td style={{ textAlign:'center', color: p.calls.overdue ? 'var(--red)' : 'var(--text2)' }}>{p.calls.overdue}</td>
            <td style={{ fontSize:12, color:'var(--text2)' }}>{remarksFor(p)}</td>
            <td><button className="btn btn-secondary btn-sm" onClick={() => onPerson(p)}>View Details</button></td>
          </tr>
        ))}</tbody>
      </table></div>
    </div>
  );
}

// ── Trends ──────────────────────────────────────────────────────────────────
function Trends({ d }) {
  const max = Math.max(1, ...(d.series || []).map(s => s.calls));
  return (
    <div className="card">
      <b style={{ fontSize:14 }}>Calls Received — last 7 days</b>
      <div style={{ display:'flex', alignItems:'flex-end', gap:14, height:220, marginTop:16 }}>
        {(d.series || []).map(s => (
          <div key={s.day} style={{ flex:1, textAlign:'center' }}>
            <div style={{ fontSize:12, fontWeight:600, marginBottom:4 }}>{s.calls}</div>
            <div style={{ height:`${Math.max(3, (s.calls / max) * 160)}px`, background:'#4F8EF7', borderRadius:'4px 4px 0 0' }} />
            <div style={{ fontSize:11, color:'var(--text2)', marginTop:5 }}>{dayLabel(s.day)}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize:11.5, color:'var(--text2)', marginTop:12 }}>
        Longer-range comparisons (this week vs last, month vs month) come with the trends phase.
      </div>
    </div>
  );
}

// ── One person's detail ─────────────────────────────────────────────────────
function PersonView({ p, range, onClose, onEvidence, remarksFor }) {
  const Row = ({ k, label }) => {
    const c = p.components[k] || { state:'na' };
    const st = STATE[c.state];
    return (
      <button onClick={() => c.state !== 'na' && onEvidence(k)}
        style={{ display:'flex', width:'100%', textAlign:'left', border:'none', background:'var(--bg3)',
          borderRadius:'var(--radius)', padding:'9px 11px', marginBottom:7, alignItems:'center', gap:10,
          cursor: c.state !== 'na' ? 'pointer' : 'default', borderLeft:`3px solid ${st.colour}` }}>
        <span style={{ fontSize:15 }}>{c.state === 'ok' ? '✅' : c.state === 'partial' ? '🟡' : c.state === 'fail' ? '❌' : '–'}</span>
        <span style={{ flex:1, fontSize:13 }}>{label}</span>
        <span style={{ fontSize:11.5, color:'var(--text2)' }}>{c.reason || st.label}</span>
      </button>
    );
  };
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:640 }}>
        <div className="modal-header">
          <span className="modal-title">{p.full_name}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div style={{ fontSize:12.5, color:'var(--text2)', marginBottom:12 }}>
          {p.campus_name} · {p.role.replace('_',' ')} · {range.from === range.to ? range.from : `${range.from} to ${range.to}`}
          · {p.working_days} working day(s)
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(110px, 1fr))', gap:10, marginBottom:14 }}>
          {[['Compliance', p.score === null ? '–' : `${p.score}%`, BAND[p.band]],
            ['Received', p.calls.received], ['Closed', p.calls.closed, 'var(--green)'],
            ['Overdue', p.calls.overdue, p.calls.overdue ? 'var(--red)' : undefined],
            ['Avg resolution', p.calls.avg_hours !== null ? `${p.calls.avg_hours}h` : '–']].map(([l, v, col]) => (
            <div key={l} className="card" style={{ margin:0, padding:'10px 12px', textAlign:'center' }}>
              <div style={{ fontSize:11, color:'var(--text2)' }}>{l}</div>
              <div style={{ fontSize:19, fontWeight:700, color: col || 'var(--text)' }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize:12, fontWeight:700, color:'var(--text2)', marginBottom:7, letterSpacing:'.04em' }}>COMPLIANCE — click any row for the records</div>
        <Row k="work_report" label="Daily work report" />
        <Row k="erp_upload" label="ERP calls uploaded" />
        <Row k="portal_activity" label="Portal activity" />
        <Row k="routines" label="Routines completed" />
        <Row k="calls" label="Call handling" />
        <div style={{ fontSize:12.5, marginTop:10, color:'var(--text2)' }}><b>Remarks:</b> {remarksFor(p)}</div>
      </div>
    </div>
  );
}

// ── Evidence behind a marker ────────────────────────────────────────────────
function Evidence({ e, range, onClose }) {
  const [rows, setRows] = useState(null);
  const [note, setNote] = useState('');
  useEffect(() => {
    api.get('/performance/evidence', { params:{ user_id:e.p.user_id, component:e.k, ...range } })
      .then(r => { setRows(r.data.rows); setNote(r.data.note || ''); })
      .catch(() => setRows([]));
  }, [e, range]);

  const title = { portal_activity:'Portal activity', routines:'Routine compliance', calls:'Call performance',
    work_report:'Daily work report', erp_upload:'ERP call upload' }[e.k];
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:820 }}>
        <div className="modal-header">
          <span className="modal-title">{title} — {e.p.full_name}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div style={{ fontSize:12.5, color:'var(--text2)', marginBottom:10 }}>
          {range.from === range.to ? range.from : `${range.from} to ${range.to}`}
          {note && <div style={{ marginTop:4 }}>{note}</div>}
        </div>
        {!rows ? <div style={{ fontSize:13, color:'var(--text2)' }}>Loading…</div>
          : !rows.length ? <div style={{ fontSize:13, color:'var(--text2)' }}>
              Nothing recorded in this period — which is exactly why this is flagged.</div>
          : (
            <div className="table-wrap" style={{ maxHeight:420, overflowY:'auto' }}><table>
              <thead><tr>{Object.keys(rows[0]).map(k => (
                <th key={k} style={{ fontSize:11 }}>{k.replace(/_/g, ' ')}</th>
              ))}</tr></thead>
              <tbody>{rows.map((r, i) => (
                <tr key={i}>{Object.values(r).map((v, j) => (
                  <td key={j} style={{ fontSize:12 }}>
                    {v === null ? '—' : typeof v === 'boolean' ? (v ? 'yes' : 'no') : String(v).slice(0, 70)}
                  </td>
                ))}</tr>
              ))}</tbody>
            </table></div>
          )}
      </div>
    </div>
  );
}
